import { actionModels as legacyActionModels } from './legacyActionModels.js';
import { getRecordTypePluralLabel, recordTypeOptions, recordTypes } from './recordTypes.js';

const toPluralLabel = (label) => {
  if (!label) {
    return '';
  }

  if (label.endsWith('s')) {
    return `${label}es`;
  }

  if (/(x|z|ch|sh)$/i.test(label)) {
    return `${label}es`;
  }

  if (/[bcdfghjklmnpqrstvwxyz]y$/i.test(label)) {
    return `${label.slice(0, -1)}ies`;
  }

  return `${label}s`;
};

const createRecordTypeActionModelShim = (label, recordTypeKey) => {
  return {
    label,
    nickname: label,
    // The curated plural map handles irregulars the naive fallback cannot.
    nicknamePlural: getRecordTypePluralLabel(recordTypeKey) || toPluralLabel(label),
    category: 'Record Type',
    fields: []
  };
};

export const recordTypeActionModels = Object.freeze(
  Object.entries(recordTypes).reduce((acc, [recordTypeKey, label]) => {
    acc[recordTypeKey] = createRecordTypeActionModelShim(label, recordTypeKey);
    return acc;
  }, {})
);

export const actionModels = {
  ...legacyActionModels,
  ...recordTypeActionModels
};

const toActionModelOption = ([value, model]) => ({
  label: model?.label || value,
  value
});

export const actionModelOptions = Object.entries(actionModels).map(toActionModelOption);
export const recordTypeActionModelOptions = [...recordTypeOptions];

export const getActionModelLabel = (modelKey) => {
  if (typeof modelKey !== 'string') {
    return modelKey;
  }

  return actionModels[modelKey]?.label || modelKey;
};

/* The plural record noun for a model key, without any category prefix:
   'contact' → 'Contacts', 'hris.employee' → 'Employees'. */
export const getActionModelPluralLabel = (modelKey) => {
  if (typeof modelKey !== 'string') {
    return modelKey;
  }

  const model = actionModels[modelKey];

  if (model?.nicknamePlural) {
    return model.nicknamePlural;
  }

  const recordNoun = String(getActionModelLabel(modelKey)).split(' / ').pop().trim();
  return toPluralLabel(recordNoun);
};

export { toPluralLabel };

export const getActionModelSelectOptions = ({ selectedModel } = {}) => {
  if (selectedModel == null) {
    return recordTypeActionModelOptions;
  }

  if (recordTypeActionModelOptions.some((option) => option.value === selectedModel)) {
    return recordTypeActionModelOptions;
  }

  const selectedOption = actionModelOptions.find((option) => option.value === selectedModel);
  if (selectedOption) {
    return [...recordTypeActionModelOptions, selectedOption];
  }

  return [
    ...recordTypeActionModelOptions,
    {
      value: selectedModel,
      label: getActionModelLabel(selectedModel)
    }
  ];
};
